import React, { useEffect, useState } from 'react';
import { Button, Form, Alert } from 'react-bootstrap';
import { useNavigate } from "react-router-dom";
import { backendURL } from '../IPaddress'; // Ensure this points to your backend server
import '../CSS/CRUDfiles.css'; // Adjust the path as necessary
import ErrorMessage from '../store/ErrorMessage';
import { useAuth } from '../store/AuthContext';
import Select from '../store/ThemedSelect';
import {
  CrudListShell,
  ExpandableRow,
  DetailGrid,
  DetailPanel,
  SummaryRow,
  FormGrid,
  FormActions,
  ScheduleEmptyState,
  ConfirmDeleteModal,
  FilterBar,
  SearchInput,
  SortControl,
  ResultSummary,
  useListControls,
} from '../components/crud';

const UHSMTA_TEACHER_SEARCH_FIELDS = ['first_name', 'last_name', 'email'];
const UHSMTA_TEACHER_SORT_OPTIONS = [
  { key: 'last_name', label: 'Last Name', accessor: (p) => p.last_name?.toLowerCase() ?? '' },
  { key: 'first_name', label: 'First Name', accessor: (p) => p.first_name?.toLowerCase() ?? '' },
  { key: 'email', label: 'Email', accessor: (p) => p.email?.toLowerCase() ?? '' },
  {
    key: 'school',
    label: 'School',
    accessor: (p, ctx) => {
      const schoolObj = ctx.schools.find((school) => school.school_id === p.school_id);
      return (schoolObj ? schoolObj.name : 'No school').toLowerCase();
    },
  },
];


function UHSMTATeacherProfile() {
  const [profiles, setProfiles] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editProfileData, setEditProfileData] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [profileToDelete, setProfileToDelete] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [openId, setOpenId] = useState(null);
  const navigate = useNavigate();
  const {authState} =useAuth();
  const [schools, setSchools] = useState([]);
  const [districts, setDistricts ] = useState([]);

  const {
    items: visibleProfiles,
    resultCount,
    totalCount,
    isFiltered,
    search,
    sort,
    clear,
  } = useListControls(profiles, {
    searchFields: UHSMTA_TEACHER_SEARCH_FIELDS,
    sortOptions: UHSMTA_TEACHER_SORT_OPTIONS,
    context: { schools },
  });



  useEffect(() => {
    if (!authState) {
        navigate('/login');
    }
    // Dependency array includes authState to react to its changes
}, [authState, navigate]);


  // Fetch Profiles
  useEffect(() => {
    fetchUHSMTATeachers();
    fetchSchools();
    fetchDistricts();
  }, []);

  const fetchUHSMTATeachers = async () => {
    try {
      const response = await fetch(`${backendURL}/api/uhsmta-teachers`, {
        method: 'GET',
        credentials: 'include', // Make sure to send credentials if your backend requires authentication
      });
      if (!response.ok) throw new Error('Failed to fetch UHSMTA teachers');
      const data = await response.json();

      if (!data){
        setErrorMessage('There are no UHSMTA teachers');
        return;
      }
      setProfiles(data);
    } catch (error) {
      setErrorMessage('Error fetching UHSMTA teachers');
    }
  };

  const fetchSchools = async () => {
    const response = await fetch(`${backendURL}/api/schools`, {
      method: 'GET',
      credentials: 'include',
    });
    const data = await response.json();
    setSchools(data);
  };
  const fetchDistricts = async () => {
    const response = await fetch(`${backendURL}/api/districts`, {
      method: 'GET',
      credentials: 'include',
    });
    const data = await response.json();
    setDistricts(data);
  };


  const handleShowFormToggle = () => {
    if (!showForm) { // Reset form only when opening the form
      resetForm();
    }
    setShowForm(!showForm);
  };
  const resetForm = () => {
    setEditProfileData(null); // Clear any existing edit data
    setErrorMessage(''); // Clear any error messages
  };

  const handleEditProfile = (profile) => {
    setEditProfileData(profile);
    setShowForm(true);
  };

  const handleDeleteConfirmation = (profileId) => {
    console.log('profile id to delete: ',profileId)
    setProfileToDelete(profileId);
    setShowModal(true);
  };

  const handleDeleteProfile = async () => {
    try {
      const response = await fetch(`${backendURL}/api/uhsmta-teachers/${profileToDelete}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!response.ok) throw new Error('Failed to delete UHSMTA teacher');
      setShowModal(false);
      fetchUHSMTATeachers(); // Refresh the list after deletion
    } catch (error) {
      console.error("Error deleting UHSMTA teacher:", error);
      setErrorMessage('This teacher is associated with an event program and all instances of their association must be deleted before this teacher can be deleted.');
      setShowModal(false);
    }
  };

  const getSchoolNameText = (schoolId) => {
    const schoolObj = schools.find(school => school.school_id === schoolId);
    return schoolObj ? schoolObj.name : 'No school';
  }

  const schoolOptions = schools.map(school_id => {
    const district = districts.find(d => d.district_id === school_id.district_id);
    const districtName = district ? district.name : 'Unknown District';

    return {
      label: `${school_id.name} (${districtName})`,
      value: school_id.school_id
    };
  });


  function formatPhoneNumber(number) {

    if(number === null) {
      return 'No number';
    }
    const numStr = number.toString();

    // This example formats the string as a US phone number
    // Adjust the substring indices based on your specific needs
    if (numStr.length === 10) { // Standard 10-digit phone number
      return `(${numStr.substring(0,3)}) ${numStr.substring(3,6)}-${numStr.substring(6)}`;
    } else if (numStr.length > 10) { // International format might need different handling
      // Example for an 11-digit number, adjust as necessary
      return `+${numStr.substring(0,1)} (${numStr.substring(1,4)}) ${numStr.substring(4,7)}-${numStr.substring(7)}`;
    }
    return numStr; // Return original string if it's not 10 or 11 digits long
  };


function ProfileForm({ editData, setErrorMessage, setShowForm, fetchUHSMTATeachers }) {
  const [ selectedSchool, setSelectedSchool ] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [ selectedDistrict, setSelectedDistrict] = useState(null);
  const [formData, setFormData] = useState({
    first_name: null,
    last_name: null,
    email: null,
    secondary_email: null,
    cell_phone_number: null,
    home_phone_number: null,
    cactus_number: null,
    street: null,
    city: null,
    state: null,
    zip: null,
    judge_status: null,
    school_id: null,
    district_id: null,
  });

  useEffect(() => {
    if (editData) {
      setFormData({
        first_name: editData.first_name || null,
        last_name: editData.last_name || null,
        email: editData.email || null,
        secondary_email: editData.secondary_email || null,
        cell_phone_number: editData.cell_phone_number || null,
        home_phone_number: editData.home_phone_number || null,
        cactus_number: editData.cactus_number || null,
        street: editData.street || null,
        city: editData.city || null,
        state: editData.state || null,
        zip: editData.zip || null,
        judge_status: editData.judge_status || null,
        school_id: editData.school_id || null,
        district_id: editData.district_id || null
      });
      let school = schoolOptions.find(school => school.value === editData.school_id)
      setSelectedSchool(school);
    }
  }, [editData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "cactus_number" || name === "cell_phone_number"){
      let validInt = parseInt(value, 10);
      if (isNaN(validInt)){
        validInt = null;
      }
      return setFormData(prev => ({...prev, [name]: validInt}));

    }
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSchoolChange = selectedOption => {
    setSelectedSchool(selectedOption);
    let schoolId = null;
    if (selectedOption) {
        schoolId = parseInt(selectedOption.value, 10);
    if (isNaN(schoolId)) {
      setErrorMessage(`Warning: could not parse school_id value "${selectedOption.value}" as an integer`);
      schoolId = null;
      }};
    setFormData({
      ...formData,
      school_id: schoolId
      });
    const selectedSchoolObj = schools.find(school_id => school_id.school_id === schoolId);
    if (selectedSchoolObj)  {
      // Find the corresponding district object
      const correspondingDistrict = districts.find(district => district.district_id === selectedSchoolObj.district_id);

    if (correspondingDistrict) {
        // Update the district in the state
        setSelectedDistrict(correspondingDistrict);

        // Update the district in the formData
        setFormData(prevFormData => ({
            ...prevFormData,
            district: correspondingDistrict.district_id
        }));
      };
    };
  };


  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log("edit data: ", editData)
    const endpoint = editData ? `${backendURL}/api/uhsmta-teachers/${editData.uhsmta_teachers_id}` : `${backendURL}/api/uhsmta-teachers`;
    const method = editData ? 'PUT' : 'POST';

    try {
      const response = await fetch(endpoint, {
        method,
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to submit the form');
      }

      fetchUHSMTATeachers(); // Refresh the profiles list
      setShowForm(false); // Close the form
      setErrorMessage(''); // Clear any previous error messages
    } catch (error) {
      console.error("Form submission error:", error);
      setErrorMessage(error.message || 'Error submitting form');
    }
  };

  const handleCancel = () => {
    setFormData({ // Reset form data to initial state
      first_name: null,
      last_name: null,
      email: null,
      secondary_email: null,
      cell_phone_number: null,
      home_phone_number: null,
      cactus_number: null,
      street: null,
      city: null,
      state: null,
      zip: null,
      judge_status: null,
      school_id: null,
      district_id: null
    });
    setErrorMessage(''); // Clear any error messages
    setShowForm(false); // Close the form
  };

  return (
    <Form onSubmit={handleSubmit}>
      <FormGrid>
        <Form.Group className="bold-label" controlId="first_name">
          <Form.Label>First Name</Form.Label>
          <Form.Control
            type="text"
            placeholder="Enter first name"
            name="first_name"
            value={formData.first_name ?? ""}
            onChange={handleChange}
            required
          />
        </Form.Group>

        <Form.Group className="bold-label" controlId="last_name">
          <Form.Label>Last Name</Form.Label>
          <Form.Control
            type="text"
            placeholder="Enter last name"
            name="last_name"
            value={formData.last_name ?? ""}
            onChange={handleChange}
            required
          />
        </Form.Group>

        <Form.Group className='bold-label' controlId='cell_phone_number'>
          <Form.Label>Cell Phone Number</Form.Label>
          <Form.Control
          type='number'
          placeholder='Enter cell phone number'
          name='cell_phone_number'
          value={formData.cell_phone_number ?? ""}
          onChange={handleChange}
          >
          </Form.Control>
        </Form.Group>

        <Form.Group className='bold-label' controlId='home_phone_number'>
          <Form.Label>Home Phone Number</Form.Label>
          <Form.Control
          type='number'
          placeholder='Enter home phone number'
          name='home_phone_number'
          value={formData.home_phone_number ?? ""}
          onChange={handleChange}
          >
          </Form.Control>
        </Form.Group>

        <Form.Group className='bold-label' controlId='cactus_number'>
          <Form.Label>Cactus Number</Form.Label>
          <Form.Control
          type='number'
          name='cactus_number'
          value={formData.cactus_number ?? ""}
          onChange={handleChange}>
          </Form.Control>
        </Form.Group>

        <Form.Group className="bold-label" controlId="email">
          <Form.Label>Email</Form.Label>
          <Form.Control
            type="email"
            placeholder="Enter email"
            name="email"
            value={formData.email ?? ""}
            onChange={handleChange}
            required
          />
        </Form.Group>
        <Form.Group className="bold-label" controlId="secondary_email">
          <Form.Label>Secondary Email</Form.Label>
          <Form.Control
            type="email"
            placeholder="Enter secondary email"
            name="secondary_email"
            value={formData.secondary_email ?? ""}
            onChange={handleChange}
          />
        </Form.Group>
        <Form.Group controlId="school_id" className="crud-form-span-full">
            <Form.Label className="bold-label">School (District) </Form.Label>
            <Select
                isSearchable={true}
                options={schoolOptions}
                value={selectedSchool}
                onChange={handleSchoolChange}
                inputId="school_id"
            />
        </Form.Group>

        <FormActions>
          <Button variant="primary" type="submit">
            Submit
          </Button>
          <Button variant="secondary" onClick={handleCancel}>
            Cancel
          </Button>
        </FormActions>
      </FormGrid>
    </Form>
  );
}


  return (
    <div className="profile-management">
      <CrudListShell
        addLabel="Add New Profile"
        listLabel="Close Form"
        showForm={showForm}
        onToggleForm={handleShowFormToggle}
        tableHeading="UHSMTA Teachers"
        filters={(
          <FilterBar
            summary={(
              <ResultSummary
                count={resultCount}
                total={totalCount}
                noun="teacher"
                onClear={clear}
                isFiltered={isFiltered}
              />
            )}
          >
            <SearchInput label="Search teachers" placeholder="Name or email" {...search} />
            <SortControl {...sort} />
          </FilterBar>
        )}
        form={(
          <ProfileForm
            editData={editProfileData}
            setErrorMessage={setErrorMessage}
            setShowForm={setShowForm}
            fetchUHSMTATeachers={fetchUHSMTATeachers}
          />
        )}
        emptyState={(
          <tr>
            <td>
              <ScheduleEmptyState>No UHSMTA teachers have been added yet.</ScheduleEmptyState>
            </td>
          </tr>
        )}
      >
        {visibleProfiles.map((profile) => (
          <ExpandableRow
            key={profile.uhsmta_teachers_id}
            isOpen={openId === profile.uhsmta_teachers_id}
            onToggle={() => setOpenId(openId === profile.uhsmta_teachers_id ? null : profile.uhsmta_teachers_id)}
            main={{
              title: `${profile.first_name} ${profile.last_name}`,
              subtitle: profile.email || 'No email on file',
            }}
            pills={[
              { label: getSchoolNameText(profile.school_id) },
              ...(profile.cactus_number ? [{ label: `Cactus: ${profile.cactus_number}` }] : []),
            ]}
            actions={(
              <>
                <Button size="sm" onClick={() => handleEditProfile(profile)} aria-label="Edit profile">
                  <i className="bi bi-pencil"></i>
                </Button>
                <Button
                  size="sm"
                  variant="danger"
                  onClick={() => handleDeleteConfirmation(profile.uhsmta_teachers_id)}
                  aria-label="Delete profile"
                >
                  <i className="bi bi-trash3"></i>
                </Button>
              </>
            )}
            detail={(
              <DetailGrid columns={2}>
                <DetailPanel title="Contact">
                  <SummaryRow label="Cell" value={formatPhoneNumber(profile.cell_phone_number)} />
                  <SummaryRow label="Home" value={formatPhoneNumber(profile.home_phone_number)} />
                  <SummaryRow label="Email" value={profile.email || 'None'} />
                  <SummaryRow label="2nd Email" value={profile.secondary_email || 'None'} />
                </DetailPanel>
                <DetailPanel title="School">
                  <SummaryRow label="School" value={getSchoolNameText(profile.school_id)} />
                  <SummaryRow label="Cactus #" value={profile.cactus_number || 'None'} />
                </DetailPanel>
              </DetailGrid>
            )}
          />
        ))}
      </CrudListShell>

      <ConfirmDeleteModal
        show={showModal}
        onHide={() => setShowModal(false)}
        onConfirm={handleDeleteProfile}
        title="Delete Profile"
        bodyText="Are you sure you want to delete this profile?"
      />

      {errorMessage && <Alert variant="danger">{errorMessage}</Alert>}
      <ErrorMessage errorMessage={errorMessage} clearErrorMessage={() => setErrorMessage()} />
    </div>
  );
}

export default UHSMTATeacherProfile;
